import { useParams, Link } from 'react-router-dom';
import { useVerifyCertificate } from '@/hooks/queries';
import { Icon, LogoMark } from '@/components';
import { ORG } from '@/config/org';
import { useEffect } from 'react';

/**
 * Public certificate verification page — the destination of every QR code.
 * No auth required; anyone can confirm a certificate against records.
 */
export function VerifyPage() {
  const { id = '' } = useParams();
  const verify = useVerifyCertificate();

  useEffect(() => {
    verify.mutate(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const result = verify.data;
  const cert = result?.certificate;
  const valid = result?.result === 'valid';

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', marginBottom: 22 }}>
          <LogoMark size={30} />
          <span style={{ fontSize: 15.5, fontWeight: 600 }}>Warm Paper ED</span>
        </div>

        <div className="card" style={{ overflow: 'hidden' }}>
          {verify.isPending || !result ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
              <div className="eyebrow">Checking hospital records…</div>
            </div>
          ) : result.result === 'not-found' ? (
            <div style={{ padding: 40, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--clay-soft)', color: 'var(--clay)', display: 'grid', placeItems: 'center' }}><Icon name="x" size={32} stroke={2.4} /></div>
              <div style={{ fontSize: 17, fontWeight: 600 }}>Certificate not found</div>
              <div className="muted" style={{ fontSize: 13 }}>No record matches <span className="u-mono">{id}</span>.</div>
            </div>
          ) : (
            cert && (
              <>
                <div style={{ padding: 24, textAlign: 'center', background: valid ? 'var(--sage-tint)' : 'var(--clay-soft)' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', margin: '0 auto 12px', background: valid ? 'var(--sage)' : 'var(--clay)', color: 'var(--on-accent)', display: 'grid', placeItems: 'center' }}>
                    <Icon name={valid ? 'check' : 'x'} size={32} stroke={2.4} />
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: valid ? 'var(--sage-deep)' : '#7e4536' }}>{valid ? 'Valid certificate' : 'Expired certificate'}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>{valid ? 'Authentic and within its validity period.' : 'Authentic, but past its expiry date.'}</div>
                </div>
                <div className="divide">
                  {([['Course', cert.course], ['Certificate ID', cert.id, true], ['Host', cert.host], ['Instructor', cert.instructor], ['Certified by', ORG.certifier.name], ['Issued', cert.issued], ['Expires', cert.expires]] as [string, string, boolean?][]).map((r, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 18px' }}>
                      <span className="muted" style={{ fontSize: 13 }}>{r[0]}</span>
                      <span style={{ fontSize: 13.5, fontWeight: 500 }} className={r[2] ? 'u-mono' : ''}>{r[1]}</span>
                    </div>
                  ))}
                </div>
              </>
            )
          )}
        </div>
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <Link to="/" className="btn btn-quiet btn-sm">Back to Warm Paper ED</Link>
        </div>
      </div>
    </div>
  );
}
